/**
 * Carga instancia según renderMode (PREVIEW → jpg/webp; DIAGNOSTIC → dcm).
 * Soporta DIRECT_URL y RANGE_ZIP (offsets en study.zip).
 * Prioriza hit en cornerstone.imageCache antes de red/IDB pipeline.
 */
import cornerstone from 'cornerstone-core'
import { RENDER_MODE } from './renderMode'
import { resolveLoadUrl, resolveDiagnosticUrl } from './renderModeUrls'
import {
  loadDicomInstanceWithCache,
  loadDicomInstanceDirectUrl,
  sopFromManifestInstance,
  fallbackCacheKey,
  fetchDicomBytes,
} from './loadDicomInstanceWithCache'
import { loadDicomFromZipRange, decodeAndCachePart10Buffer } from './loadDicomFromZipRange'
import { loadPreviewInstance } from './loadPreviewInstance'
import { ensureImageRender } from './ensureImageRender'
import { DICOM_FETCH_TYPE, isReportLooseSeriesInst } from './manifestNormalize'
import {
  isNonImageSeriesInst,
  isPdfDocumentSeriesInst,
} from './isNonImageInstance'

/**
 * imageId esperado según modo (preview https o wadouri:url).
 * @param {{ kind: string, url: string|null }} resolved
 * @returns {string|null}
 */
function imageIdForResolved(resolved) {
  if (!resolved || !resolved.url) return null
  const url = String(resolved.url)
  if (resolved.kind === 'preview') return url
  return /^wadouri:/i.test(url) ? url : `wadouri:${url}`
}

/**
 * ¿Usar fetch RANGE sobre study.zip?
 * @param {object} inst
 * @param {{ dicomFetchType?: string }} [resolved]
 * @param {object|null} [series]
 */
function isRangeZipLoad(inst, resolved, series) {
  if (!inst) return false
  if (isReportLooseSeriesInst(series || null, inst)) return false
  if (inst.dicomFetchType === DICOM_FETCH_TYPE.DIRECT_URL) return false
  if (resolved && resolved.dicomFetchType === DICOM_FETCH_TYPE.DIRECT_URL) {
    return false
  }
  const wantsRange =
    (resolved && resolved.dicomFetchType === DICOM_FETCH_TYPE.RANGE_ZIP) ||
    inst.dicomFetchType === DICOM_FETCH_TYPE.RANGE_ZIP
  if (!wantsRange) return false
  return (
    !!inst.archiveUrl &&
    inst.byteOffset != null &&
    inst.byteLength != null &&
    Number(inst.byteLength) > 0
  )
}

/**
 * Si Cornerstone ya tiene el imageLoadObject, rehidrata sin red.
 * @param {string} imageId
 * @param {'preview'|'dicom'} renderKind
 * @returns {Promise<object|null>}
 */
async function tryCornerstoneImageCache(imageId, renderKind) {
  if (!imageId || !cornerstone.imageCache) return null
  try {
    if (typeof cornerstone.imageCache.getImageLoadObject !== 'function') {
      return null
    }
    const loadObject = cornerstone.imageCache.getImageLoadObject(imageId)
    if (!loadObject) return null
    let image = await cornerstone.loadAndCacheImage(imageId)
    image = ensureImageRender(image, {
      renderKind: renderKind === 'preview' ? 'preview' : 'dicom',
    })
    return image
  } catch (e) {
    return null
  }
}

/**
 * Carga DICOM vía Range zip o URL directa.
 * @param {object} opts
 */
async function loadDicomBranch(opts) {
  const inst = opts.inst
  const mode = opts.renderMode || RENDER_MODE.DIAGNOSTIC
  const usedFallback = mode === RENDER_MODE.PREVIEW
  const skipIdbRead = !!opts.skipIdbRead
  const skipIdbWrite = !!opts.skipIdbWrite

  if (isRangeZipLoad(inst, opts.resolvedPlan, opts.series || null)) {
    const loaded = await loadDicomFromZipRange({
      archiveUrl: inst.archiveUrl,
      byteOffset: inst.byteOffset,
      byteLength: inst.byteLength,
      inst,
      series: opts.series || null,
      studyInstanceUID: opts.studyInstanceUID,
      dicomUrlFallback: resolveDiagnosticUrl(inst, opts.assetBaseUrl || null),
      fetchHeaders: opts.fetchHeaders,
      skipIdbRead,
      skipIdbWrite,
    })
    return {
      ...loaded,
      renderKind: loaded.renderKind || loaded.loadKind || 'dicom',
      usedFallback,
      loadKind: loaded.loadKind || 'dicom',
    }
  }

  const dicomUrl =
    (opts.resolvedPlan && opts.resolvedPlan.url) ||
    resolveDiagnosticUrl(inst, opts.assetBaseUrl || null) ||
    opts.absoluteUrl

  if (!dicomUrl) {
    throw new Error('Sin URL DICOM resoluble')
  }

  // Encapsulated PDF / PR/SR/KO: GET Part-10 → buffer (sin decode Cornerstone)
  const series = opts.series || null
  const isPdf = isPdfDocumentSeriesInst(series, inst)
  const nonImage = isPdf || isNonImageSeriesInst(series, inst)
  if (nonImage) {
    const fetched = await fetchDicomBytes(dicomUrl, {
      headers: opts.fetchHeaders,
      credentials: 'omit',
    })
    if (!fetched.complete || !fetched.buffer || fetched.buffer.byteLength === 0) {
      throw new Error(
        `Descarga informe incompleta (${(fetched.buffer && fetched.buffer.byteLength) || 0} bytes)`
      )
    }
    const loaded = await decodeAndCachePart10Buffer({
      buffer: fetched.buffer,
      inst,
      series,
      studyInstanceUID: opts.studyInstanceUID,
      absoluteUrlTag: dicomUrl,
      skipIdbWrite,
      fromCache: false,
    })
    return {
      ...loaded,
      renderKind: loaded.renderKind || loaded.loadKind || (isPdf ? 'pdf' : 'nonimage'),
      usedFallback,
      loadKind: loaded.loadKind || (isPdf ? 'pdf' : 'nonimage'),
      absoluteUrl: dicomUrl,
    }
  }

  const dicomImageId = /^wadouri:/i.test(dicomUrl) ? dicomUrl : `wadouri:${dicomUrl}`
  if (!skipIdbRead) {
    const cachedDicom = await tryCornerstoneImageCache(dicomImageId, 'dicom')
    if (cachedDicom) {
      const sop =
        sopFromManifestInstance(inst) || fallbackCacheKey(dicomImageId, inst)
      return {
        image: cachedDicom,
        imageId: dicomImageId,
        sopInstanceUID: sop ? String(sop) : null,
        fromCache: true,
        renderKind: 'dicom',
        usedFallback,
        loadKind: 'dicom',
        absoluteUrl: dicomUrl,
      }
    }
  }

  try {
    const loaded = await loadDicomInstanceWithCache({
      absoluteUrl: dicomUrl,
      inst,
      studyInstanceUID: opts.studyInstanceUID,
      fetchHeaders: opts.fetchHeaders,
      skipIdbRead,
      skipIdbWrite,
    })
    return {
      ...loaded,
      renderKind: 'dicom',
      usedFallback,
      loadKind: 'dicom',
      absoluteUrl: dicomUrl,
    }
  } catch (cacheErr) {
    console.warn('loadInstanceForMode: cache path failed, direct', cacheErr)
    const direct = await loadDicomInstanceDirectUrl(dicomUrl)
    return {
      ...direct,
      renderKind: 'dicom',
      usedFallback,
      loadKind: 'dicom',
      absoluteUrl: dicomUrl,
    }
  }
}

/**
 * @param {object} opts
 * @param {object} opts.inst
 * @param {string|null} [opts.assetBaseUrl]
 * @param {string} [opts.studyInstanceUID]
 * @param {'DIAGNOSTIC'|'PREVIEW'} [opts.renderMode]
 * @param {Record<string,string>} [opts.fetchHeaders]
 * @param {string} [opts.absoluteUrl] legacy pre-resolved dicom url
 * @param {boolean} [opts.skipIdbRead]
 * @param {boolean} [opts.skipIdbWrite]
 */
export async function loadInstanceForMode(opts) {
  const mode = opts.renderMode || RENDER_MODE.DIAGNOSTIC
  const skipIdbRead = !!opts.skipIdbRead
  const skipIdbWrite = !!opts.skipIdbWrite
  const nonImage = isNonImageSeriesInst(opts.series || null, opts.inst || null)

  // PR/GSPS/SR: nunca preview web ni cornerstone image cache de píxeles.
  if (nonImage) {
    return loadDicomBranch({
      ...opts,
      renderMode: mode,
      resolvedPlan: resolveLoadUrl(opts.inst, RENDER_MODE.DIAGNOSTIC, opts.assetBaseUrl || null),
      skipIdbRead,
      skipIdbWrite,
    })
  }

  const resolved = resolveLoadUrl(opts.inst, mode, opts.assetBaseUrl || null)
  const triedPreview = resolved.kind === 'preview' && !!resolved.url

  // Solo reusar imageCache de cornerstone si no forzamos bypass
  if (!skipIdbRead) {
    const cacheImageId = imageIdForResolved(resolved)
    if (cacheImageId) {
      const renderKind = resolved.kind === 'preview' ? 'preview' : 'dicom'
      const cachedImage = await tryCornerstoneImageCache(cacheImageId, renderKind)
      if (cachedImage) {
        const sop =
          sopFromManifestInstance(opts.inst) ||
          fallbackCacheKey(cacheImageId, opts.inst)
        return {
          image: cachedImage,
          imageId: cacheImageId,
          sopInstanceUID: sop ? String(sop) : null,
          fromCache: true,
          renderKind,
          usedFallback: false,
          loadKind: renderKind,
          absoluteUrl: resolved.url,
        }
      }
    }
  }

  if (triedPreview) {
    try {
      const loaded = await loadPreviewInstance({
        previewUrl: resolved.url,
        inst: opts.inst,
        studyInstanceUID: opts.studyInstanceUID,
        skipIdbRead,
        skipIdbWrite,
      })
      return {
        ...loaded,
        usedFallback: false,
        loadKind: 'preview',
        absoluteUrl: resolved.url,
      }
    } catch (e) {
      console.warn('loadInstanceForMode: preview failed, fallback dcm', e)
    }
  }

  // DICOM: RANGE_ZIP o DIRECT (re-resolver plan DIAG; no reusar URL preview)
  return loadDicomBranch({
    ...opts,
    renderMode: mode,
    resolvedPlan: resolveLoadUrl(
      opts.inst,
      RENDER_MODE.DIAGNOSTIC,
      opts.assetBaseUrl || null
    ),
    skipIdbRead,
    skipIdbWrite,
  })
}
